import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import api from "@/lib/api";
import { catLabel } from "@/lib/constants";
import { Drama, Shield, Stethoscope, Skull, Scale, Briefcase, Heart, Video, Play } from "lucide-react";

const META = {
  police:   { icon: Shield,      label: "Police",         color: "#3B82F6", intro: "Officers, inspectors, uniformed roles" },
  doctor:   { icon: Stethoscope, label: "Doctor",         color: "#10B981", intro: "Doctors, nurses, medical drama" },
  villain:  { icon: Skull,       label: "Villain",        color: "#DC2626", intro: "Antagonists, gangsters, dark roles" },
  lawyer:   { icon: Scale,       label: "Lawyer",         color: "#F5C462", intro: "Advocates, judges, courtroom drama" },
  ceo:      { icon: Briefcase,   label: "CEO",            color: "#A78BFA", intro: "Corporate leads, boardroom characters" },
  romantic: { icon: Heart,       label: "Romantic Lead",  color: "#F472B6", intro: "Romantic hero/heroine performances" },
};

export default function DiscoverCharacter() {
  const { character } = useParams();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const m = META[character] || { icon: Drama, label: character || "Character", color: "#F5C462", intro: "Character-specific performances" };
  const Icon = m.icon;
  useEffect(() => { (async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/videos/by-character?character=${encodeURIComponent(character)}`);
      setVideos(data.videos || []);
    } catch {}
    finally { setLoading(false); }
  })(); }, [character]);
  return (
    <div data-testid="discover-character-page" className="min-h-screen pb-24">
      <TopBar subtitle="Character"/>
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        <BackButton fallback="/app/discover" label="Back"/>
        <div className="rounded-2xl border p-4" style={{borderColor: `${m.color}60`, background: `linear-gradient(135deg, ${m.color}18, #0A0605)`}}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center border-2" style={{backgroundColor: `${m.color}25`, borderColor: `${m.color}80`, color: m.color}}>
              <Icon size={22} strokeWidth={2.25}/>
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">{m.label}</h1>
              <p className="text-xs text-zinc-400">{m.intro}</p>
            </div>
          </div>
        </div>
        {loading ? (
          <p className="text-sm text-zinc-500 text-center py-10">Loading performances...</p>
        ) : videos.length === 0 ? (
          <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-6 text-center">
            <p className="text-sm text-zinc-300">No videos tagged for <span className="text-white font-semibold">{m.label}</span> yet.</p>
            <p className="text-[11px] text-zinc-500 mt-2">Creators — add "#{character}" to your title or tags to show up here.</p>
            <Link to="/app/upload" className="inline-flex items-center gap-1 mt-4 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white text-sm px-5 h-10">
              <Video size={14}/> Upload a performance
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {videos.map(v => (
              <Link key={v.id} to={`/v/${v.id}`} data-testid={`char-video-${v.id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 hover:border-[#DC2626]/40 transition-colors">
                <div className="aspect-video rounded-lg bg-black relative mb-2 flex items-center justify-center overflow-hidden">
                  {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover"/> : <Play size={24} className="text-zinc-600"/>}
                </div>
                <p className="text-sm font-semibold text-white line-clamp-1">{v.title || "Untitled"}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">@{v.username} · {catLabel(v.category)}</p>
                <p className="text-[10px] text-zinc-600 mt-0.5">{v.views || 0} views · {v.likes || 0} likes</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
